import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, User, Phone, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function AuthPage({ defaultTab = "login" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "/dashboard";
  const prefilledEmail = searchParams.get("email") || "";
  const { login, register, user } = useAuth();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const [loginForm, setLoginForm] = useState({ email: prefilledEmail, password: "" });
  const [registerForm, setRegisterForm] = useState({
    email: prefilledEmail,
    password: "",
    confirmPassword: "",
    fullName: "",
    phone: ""
  });

  // Redirect if already logged in — honor ?next= so users returning from
  // the Buying Power → Qualify flow land on their premium results.
  if (user) {
    navigate(nextPath);
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      navigate(nextPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerForm.email || !registerForm.password || !registerForm.fullName) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (registerForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);
    try {
      const result = await register(
        registerForm.email,
        registerForm.password,
        registerForm.fullName,
        registerForm.phone || null
      );
      setRegisteredEmail(registerForm.email);
      setRegistrationSuccess(true);
      toast.success("Check your email to verify your account!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Show email verification success screen
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="card-base text-center py-10">
            <div className="w-16 h-16 bg-[#52BFB8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[#52BFB8]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
            <p className="text-gray-600 mb-6">
              We've sent a verification link to:
              <br />
              <strong className="text-gray-900">{registeredEmail}</strong>
            </p>
            <div className="bg-blue-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-blue-800">
                <strong>Next steps:</strong>
              </p>
              <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                <li>Open the email from Financify</li>
                <li>Click "Verify Email Address"</li>
                <li>You'll be logged in automatically</li>
              </ol>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Didn't receive the email? Check your spam folder or{" "}
              <Link to="/verify-email" className="text-[#52BFB8] hover:underline">
                request a new link
              </Link>
            </p>
            <Button 
              variant="outline" 
              onClick={() => {
                setRegistrationSuccess(false);
                setActiveTab("login");
              }}
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="heading-2 mb-2">Track Your Applications</h1>
          <p className="text-gray-600">Sign in or create an account to save and track your loan applications.</p>
        </div>

        <div className="card-base">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Create Account</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="login-email"
                      type="email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                      className="pl-10"
                      placeholder="your@email.com"
                      data-testid="login-email"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="login-password"
                      type="password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                      className="pl-10"
                      placeholder="Enter password"
                      data-testid="login-password"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="login-submit">
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign In<ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
                <div className="text-center mt-3">
                  <Link to="/forgot-password" className="text-sm text-[#52BFB8] hover:underline" data-testid="forgot-password-link">
                    Forgot your password?
                  </Link>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name">Full Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input id="register-name" type="text" value={registerForm.fullName} onChange={(e) => setRegisterForm({...registerForm, fullName: e.target.value})} className="pl-10" placeholder="John Smith" data-testid="register-name" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-email">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input id="register-email" type="email" value={registerForm.email} onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})} className="pl-10" placeholder="your@email.com" data-testid="register-email" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-phone">Phone (optional)</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input id="register-phone" type="tel" value={registerForm.phone} onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})} className="pl-10" placeholder="242-555-1234" data-testid="register-phone" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-password">Password *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input id="register-password" type="password" value={registerForm.password} onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})} className="pl-10" placeholder="Min 6 characters" data-testid="register-password" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="register-confirm">Confirm Password *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input id="register-confirm" type="password" value={registerForm.confirmPassword} onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})} className="pl-10" placeholder="Confirm password" data-testid="register-confirm" />
                  </div>
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="register-submit">
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Account<ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
        <p className="text-center text-sm text-gray-500 mt-6">By signing up, you agree to our <Link to="/disclaimer" className="text-[#52BFB8] hover:underline">Terms & Privacy Policy</Link></p>
      </div>
    </div>
  );
}
